import { motion } from "framer-motion";
import {
  BarChart3,
  Shield,
  TrendingUp,
  Users,
  MessageSquare,
  Zap,
  Gift,
  Coins,
  Bot,
  Ticket,
  Settings,
  ArrowLeft,
  Home,
  Loader2,
  Check,
  Star,
  Megaphone,
  Sparkles,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { pluginApi } from "@/lib/api";
import { useServer, usePlugins } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApi";
import { toast } from "sonner";
import React, { useMemo, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";

interface PluginCardProps {
  id: string;
  label: string;
  icon: React.ElementType;
  category: string;
  description: string;
  enabled?: boolean;
  onSelect: (id: string) => void;
  onToggle?: (id: string) => void;
  isLoading?: boolean;
  isToggling?: boolean;
  isDemo?: boolean;
  canToggle?: boolean;
}

const categoryAccentClass: Record<string, string> = {
  moderation: "from-orange-500/25 to-orange-500/10 text-orange-400",
  engagement: "from-yellow-500/25 to-yellow-500/10 text-yellow-400",
  automation: "from-sky-500/25 to-sky-500/10 text-sky-400",
  economy: "from-lime-500/25 to-lime-500/10 text-lime-400",
  support: "from-indigo-500/25 to-indigo-500/10 text-indigo-400",
  ai: "from-fuchsia-500/25 to-fuchsia-500/10 text-fuchsia-400",
  media: "from-cyan-500/25 to-cyan-500/10 text-cyan-400",
  analytics: "from-emerald-500/25 to-emerald-500/10 text-emerald-400",
  utility: "from-slate-500/25 to-slate-500/10 text-slate-300",
};

const categoryFilterClass: Record<string, string> = {
  all: "bg-primary text-primary-foreground",
  moderation: "bg-orange-500/20 text-orange-300 border border-orange-400/30 hover:bg-orange-500/30",
  engagement: "bg-yellow-500/20 text-yellow-300 border border-yellow-400/30 hover:bg-yellow-500/30",
  automation: "bg-sky-500/20 text-sky-300 border border-sky-400/30 hover:bg-sky-500/30",
  economy: "bg-lime-500/20 text-lime-300 border border-lime-400/30 hover:bg-lime-500/30",
  support: "bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 hover:bg-indigo-500/30",
  ai: "bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30 hover:bg-fuchsia-500/30",
  media: "bg-cyan-500/20 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-500/30",
  analytics: "bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-500/30",
  utility: "bg-slate-500/20 text-slate-200 border border-slate-400/30 hover:bg-slate-500/30",
};

const PluginCard = ({ id, label, icon: Icon, category, description, enabled, onSelect, onToggle, isLoading, isToggling, isDemo, canToggle }: PluginCardProps) => {
  const { t } = useI18n();
  const accentClass = categoryAccentClass[category] || "from-primary/20 to-primary/10 text-primary";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -4 }}
    >
      <Card
        className="bg-card border border-border rounded-2xl shadow-sm transition-all duration-300 cursor-pointer hover:shadow-lg hover:border-primary/40 h-full"
        onClick={() => !isLoading && !isDemo && onSelect(id)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className={cn("w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center flex-shrink-0", accentClass)}>
              <Icon className="w-6 h-6" />
            </div>
            {enabled && !isDemo && (
              <div className="flex-shrink-0">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-500" />
                </div>
              </div>
            )}
            {isDemo && (
              <div className="flex-shrink-0">
                <div className="px-2 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase">
                  Demo
                </div>
              </div>
            )}
          </div>
          <CardTitle className="text-lg">{label}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <CardDescription className="text-sm line-clamp-2">
            {description}
          </CardDescription>

          <Button
            className="w-full"
            variant={enabled && !isDemo && canToggle ? "default" : "outline"}
            disabled={isLoading || isDemo || isToggling}
            onClick={(e) => {
              e.stopPropagation();
              if (isDemo) return;
              if (canToggle && onToggle) {
                onToggle(id);
                return;
              }
              onSelect(id);
            }}
          >
            {isLoading || isToggling ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('management.loading')}
              </span>
            ) : isDemo ? (
              "Demo"
            ) : canToggle ? (
              enabled ? t('management.disable') : t('management.enable')
            ) : (
              t('management.configure')
            )}
          </Button>

          {/* Status Indicator */}
          <div className="flex items-center justify-center gap-2 text-xs">
            <div
              className={cn(
                "w-2 h-2 rounded-full",
                isDemo ? "bg-primary" : enabled ? "bg-green-500" : "bg-gray-400"
              )}
            />
            <span className="text-muted-foreground">
              {isDemo ? "Demo" : enabled ? t('management.enabled') : t('management.disabled')}
            </span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface ServerManagementProps {
  serverId: string;
  onBack: () => void;
  onHome: () => void;
  onSelectPlugin: (pluginId: string) => void;
  selectedServer: any;
  activeCategory: string;
  onCategoryChange: (categoryId: string) => void;
}

export const ServerManagement = ({
  serverId,
  onBack,
  onHome,
  onSelectPlugin,
  selectedServer,
  activeCategory,
  onCategoryChange,
}: ServerManagementProps) => {
  const { t } = useI18n();
  const [togglingPluginId, setTogglingPluginId] = useState<string | null>(null);

  const { data: plugins = [], isLoading: pluginsLoading } = usePlugins(serverId) as {
    data: any[];
    isLoading: boolean;
  };

  const resolveBackendPluginId = (uiPluginId: string): string | null => {
    if (uiPluginId === 'commands') return 'custom-commands';
    if (uiPluginId === 'giveaway') return 'giveaways';
    if (uiPluginId === 'overview' || uiPluginId === 'settings') return null;
    return uiPluginId;
  };

  const { mutate: togglePlugin } = useApiMutation(
    ({ uiPluginId, enabled }: { uiPluginId: string; enabled: boolean }) => {
      const backendPluginId = resolveBackendPluginId(uiPluginId);
      if (!backendPluginId) {
        throw new Error('Questo elemento non è un plugin attivabile');
      }
      return enabled
        ? pluginApi.disablePlugin(serverId, backendPluginId)
        : pluginApi.enablePlugin(serverId, backendPluginId);
    },
    {
      invalidateQueries: [['servers', serverId, 'plugins']],
      onSuccess: () => toast.success(t('management.toggleUpdated')),
      onError: (error: any) => toast.error(error?.message || t('management.toggleError')),
      onSettled: () => setTogglingPluginId(null),
    }
  );

  const categories = [
    { id: "all", label: t('management.all') },
    { id: "moderation", label: "Moderation" },
    { id: "engagement", label: "Engagement" },
    { id: "automation", label: "Automation" },
    { id: "economy", label: "Economy" },
    { id: "support", label: "Support" },
    { id: "ai", label: "AI" },
    { id: "media", label: "Media" },
    { id: "analytics", label: "Analytics" },
    { id: "utility", label: "Utility" },
  ];

  const pluginsList = [
    { 
      id: "overview", 
      label: t('management.overview'), 
      icon: BarChart3,
      description: t('management.overviewDesc'),
      demo: false,
      category: "analytics"
    },
    { 
      id: "moderation", 
      label: "Moderation", 
      icon: Shield,
      description: "Gestisci avvertimenti e azioni di moderazione automatiche",
      demo: false,
      category: "moderation"
    },
    { 
      id: "automod", 
      label: "Auto-Moderation", 
      icon: Shield,
      description: "Configurazione dei filtri e della moderazione automatica",
      demo: false,
      category: "moderation"
    },
    { 
      id: "leveling", 
      label: "Leveling", 
      icon: TrendingUp,
      description: "Sistema di livelli e XP per i membri del server",
      demo: false,
      category: "engagement"
    },
    { 
      id: "welcome", 
      label: "Welcome", 
      icon: Users,
      description: t('management.welcomeDesc'),
      demo: false,
      category: "engagement"
    },
    {
      id: "welcome-designer",
      label: "Welcome Designer",
      icon: Sparkles,
      description: "Builder visuale con blocchi tema, sezioni e preview live",
      demo: false,
      category: "engagement"
    },
    { 
      id: "commands", 
      label: "Custom Commands", 
      icon: MessageSquare,
      description: "Crea e gestisci comandi personalizzati per il server",
      demo: false,
      category: "automation"
    },
    { 
      id: "reaction-roles", 
      label: "Reaction Roles", 
      icon: Zap,
      description: "Permetti ai member di auto-assegnarsi ruoli tramite reazioni",
      demo: false,
      category: "engagement"
    },
    { 
      id: "giveaway", 
      label: "Giveaway", 
      icon: Gift,
      description: "Organizza concorsi e giveaway nel server",
      demo: false,
      category: "engagement"
    },
    { 
      id: "economy", 
      label: "Economy", 
      icon: Coins,
      description: "Sistema economico con moneta virtuale e negozio",
      demo: false,
      category: "economy"
    },
    { 
      id: "ai-helper", 
      label: "AI Helper", 
      icon: Bot,
      description: "Integrazione con AI per risposte intelligenti",
      demo: false,
      category: "ai"
    },
    { 
      id: "tickets", 
      label: "Tickets", 
      icon: Ticket,
      description: "Sistema di ticket per il supporto e le segnalazioni",
      demo: false,
      category: "support"
    },
    { 
      id: "settings", 
      label: "Settings", 
      icon: Settings,
      description: "Impostazioni generali e configurazione del server",
      demo: false,
      category: "utility"
    },
    {
      id: "logging",
      label: "Logging Avanzato",
      icon: BarChart3,
      description: "Log completi: messaggi, joins/leaves, invites, edits, deletes",
      demo: false,
      category: "moderation"
    },
    {
      id: "social-alerts",
      label: "Social Alerts",
      icon: Users,
      description: "Twitch/YouTube/Twitter/Instagram notifications",
      demo: false,
      category: "engagement"
    },
    {
      id: "anti-raid",
      label: "Anti-Raid",
      icon: Shield,
      description: "Protezione raid con rate limit e azioni automatiche",
      demo: false,
      category: "moderation"
    },
    {
      id: "sticky-roles",
      label: "Sticky Roles",
      icon: Users,
      description: "Ripristina ruoli automaticamente al rejoin",
      demo: false,
      category: "moderation"
    },
    {
      id: "bulk-roles",
      label: "Bulk Role Management",
      icon: Users,
      description: "Assegnazioni massive con filtri",
      demo: false,
      category: "moderation"
    },
    {
      id: "suggestions",
      label: "Suggestions",
      icon: MessageSquare,
      description: "Suggerimenti con voti e modalità anonima",
      demo: false,
      category: "engagement"
    },
    {
      id: "starboard",
      label: "Starboard",
      icon: Star,
      description: "Metti in evidenza i messaggi con ⭐",
      demo: false,
      category: "engagement"
    },
    {
      id: "polls",
      label: "Polls",
      icon: TrendingUp,
      description: "Sondaggi avanzati con risultati live",
      demo: false,
      category: "engagement"
    },
    {
      id: "automations",
      label: "Automations",
      icon: Settings,
      description: "Trigger/condizioni/azioni personalizzate",
      demo: false,
      category: "automation"
    },
    {
      id: "setup-assistant",
      label: "Setup Assistant",
      icon: Settings,
      description: "Crea automaticamente canali e ruoli mancanti per i plugin attivi",
      demo: false,
      category: "automation"
    },
    {
      id: "ai-interview",
      label: "AI Interview",
      icon: Bot,
      description: "Whitelist AI con verifica risposte, codice DM e assegnazione ruolo",
      demo: false,
      category: "ai"
    },
    {
      id: "ai-server-architect",
      label: "AI Server Architect",
      icon: Bot,
      description: "Agente AI Gemini: chat, piano modifiche live e creazione server completa",
      demo: false,
      category: "ai"
    },
    {
      id: "bot-personalizer",
      label: "Bot Personalizer",
      icon: Bot,
      description: "Avatar/nome/status/backstory AI",
      demo: false,
      category: "ai"
    },
    {
      id: "voice-recording",
      label: "Voice Recording",
      icon: Ticket,
      description: "Registrazione audio canali vocali",
      demo: false,
      category: "media"
    },
    {
      id: "web3-gating",
      label: "Verification",
      icon: Shield,
      description: t('management.verificationDesc'),
      demo: false,
      category: "utility"
    },
    {
      id: "accepted-members",
      label: "Accepted Members",
      icon: Users,
      description: t('management.acceptedDesc'),
      demo: false,
      category: "moderation"
    },
    {
      id: "backups",
      label: "Server Backups",
      icon: Settings,
      description: "Backup e restore canali/ruoli/templates",
      demo: false,
      category: "utility"
    },
    { 
      id: "advertising", 
      label: "Advertising", 
      icon: Megaphone,
      description: "Crea embed promozionali con CTA e immagini",
      demo: false,
      category: "utility"
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: BarChart3,
      description: "Statistiche attività, messaggi, trend",
      demo: false,
      category: "analytics"
    },
    {
      id: "member-explorer",
      label: "Member Explorer",
      icon: Users,
      description: "Visualizza i membri e a quali altri server appartengono. Identifica membri in server blacklisted.",
      demo: false,
      category: "moderation"
    },
    {
      id: "server-blacklist",
      label: "Server Blacklist",
      icon: Shield,
      description: "Gestisci un blacklist di server non consoni. Avverti gli utenti se sono nei server blacklisted.",
      demo: false,
      category: "moderation"
    },
    {
      id: "rules",
      label: "Rules",
      icon: Shield,
      description: "Gestione regolamenti del server e pubblicazione regole",
      demo: false,
      category: "utility"
    }
  ];

  const filteredPlugins = useMemo(
    () => pluginsList.filter((p: any) => activeCategory === "all" || p.category === activeCategory),
    [activeCategory, pluginsList]
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-background to-muted/30"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <header className="h-16 border-b border-border flex items-center gap-4 px-6 sticky top-0 z-10 bg-background/80 backdrop-blur-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="hover:bg-muted"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={onHome}
          className="hover:bg-muted"
        >
          <Home className="w-5 h-5" />
        </Button>

        <div className="flex-1">
          <h1 className="text-xl font-bold">{t('management.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {selectedServer?.name || t('management.server')}
          </p>
        </div>

        {selectedServer?.icon && (
          <img
            src={`https://cdn.discordapp.com/icons/${selectedServer.discordId}/${selectedServer.icon}.png`}
            alt={selectedServer.name}
            className="w-10 h-10 rounded-lg"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        )}
      </header>

      {/* Main Content */}
      <main className="page-container mx-auto py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Plugin & Funzionalità</h2>
          <p className="text-muted-foreground">
            Seleziona un plugin per configurarlo e personalizzarlo
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => onCategoryChange(cat.id)}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all",
                activeCategory === cat.id
                  ? (cat.id === "all" ? categoryFilterClass.all : categoryFilterClass[cat.id] || categoryFilterClass.all)
                  : (cat.id === "all"
                    ? "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                    : "bg-muted/60 text-muted-foreground border border-border hover:text-foreground hover:border-white/20")
              )}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Plugins Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredPlugins.map((plugin: any) => {
            const backendPluginId = resolveBackendPluginId(plugin.id);
            const isEnabled = plugins.some(
              (p: any) => p.pluginId === backendPluginId && p.enabled
            );
            const canToggle = !!backendPluginId;
            const isToggling = togglingPluginId === plugin.id;

            return (
              <PluginCard
                key={plugin.id}
                id={plugin.id}
                label={plugin.label}
                icon={plugin.icon}
                category={plugin.category}
                description={plugin.description}
                enabled={isEnabled}
                isDemo={plugin.demo}
                onSelect={onSelectPlugin}
                onToggle={(uiPluginId) => {
                  setTogglingPluginId(uiPluginId);
                  togglePlugin({ uiPluginId, enabled: isEnabled });
                }}
                isLoading={pluginsLoading}
                isToggling={isToggling}
                canToggle={canToggle}
              />
            );
          })}
        </motion.div>

        {/* Info Box */}
        <motion.div
          className="mt-12 p-6 rounded-2xl bg-primary/5 border border-primary/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="font-semibold mb-2">💡 Suggerimento</h3>
          <p className="text-sm text-muted-foreground">
            Clicca su un plugin per visualizzare e modificare le sue impostazioni. 
            I plugin contrassegnati con un badge verde sono già abilitati nel server.
          </p>
        </motion.div>
      </main>
    </motion.div>
  );
};
